import React, { useCallback, useState } from "react";
import { observer } from "mobx-react";
import { ItemsContainer } from "./Items.styled";
import { Typography } from "@ann/web-components";
import EmailItem from "../../../../components/itemList/EmailItemList";
import NoteItem from "../../../../components/itemList/NotesItemList";
import SMSItem from "../../../../components/itemList/SMSItemList";
import EmailSlideOut from "../../../../components/PreviewSlideOut/EmailSlideOut";
import NotesSlideOut from "../../../../components/PreviewSlideOut/NotesSlideOut";
import SMSSlideOut from "../../../../components/PreviewSlideOut/SMSSlideOut";

const Items = observer(({ 
  items = [],
  handleGetNoteDetail,
  handleUpdateNote,
  handleDeleteNote,
}) => {
  const [showEmailSlideOut, setShowEmailSlideOut] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showNotesSlideOut, setShowNotesSlideOut] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showSMSSlideOut, setShowSMSSlideOut] = useState(false);
  const [selectedSMS, setSelectedSMS] = useState(null);

  const handleOpenEmailSlideOut = useCallback((email) => {
    setSelectedEmail(email);
    setShowEmailSlideOut(true);
  }, []);

  const handleCloseEmailSlideOut = useCallback(() => {
    setShowEmailSlideOut(false);
    setSelectedEmail(null);
  }, []);

  const handleOpenSMSSlideOut = useCallback((sms) => {
    setSelectedSMS(sms);
    setShowSMSSlideOut(true);
  }, []);

  const handleCloseSMSSlideOut = useCallback(() => {
    setShowSMSSlideOut(false);
    setSelectedSMS(null);
  }, []);

  const handleOpenNotesSlideOut = useCallback(
    async (note) => {
      try {
        const noteUuid =
          note.note_uuid ||
          note.uuid ||
          note.raw?.note_uuid ||
          note.raw?.uuid ||
          note.raw?.id ||
          note.raw?.noteId ||
          note.raw?.activity_uuid ||
          null;

        const baseNoteData = {
          note_uuid: noteUuid,
          note_title: note.title || "",
          note_content: note.description || "",
          category: note.category || "",
          last_update: note.lastUpdated || null,
          created_at: note.creationDate || null,
          raw: note.raw,
        };

        setSelectedNote(baseNoteData);
        setShowNotesSlideOut(true);

        if (noteUuid) {
          try {
            const fullNoteData = await handleGetNoteDetail(noteUuid);
            setSelectedNote((prev) => ({
              ...prev,
              ...fullNoteData,
              note_uuid: fullNoteData.note_uuid || noteUuid,
              raw: fullNoteData.raw || prev.raw,
            }));
          } catch (error) {
            console.error("[All Items] Error loading note detail, using base data:", error);
          }
        }
      } catch (error) {
        console.error("[All Items] Error opening note for update:", error);
      }
    },
    [handleGetNoteDetail]
  );

  const handleCloseNotesSlideOut = useCallback(() => {
    setShowNotesSlideOut(false);
    setSelectedNote(null);
  }, []);

  const handleDeleteNoteClick = useCallback(
    async (note) => {
      try {
        const noteUuid =
          note.note_uuid ||
          note.uuid ||
          note.raw?.note_uuid ||
          note.raw?.uuid ||
          note.raw?.id ||
          note.raw?.noteId ||
          note.raw?.activity_uuid ||
          null;

        if (!noteUuid) {
          console.error("[All Items] Cannot delete note: note_uuid not found", note);
          return;
        }

        await handleDeleteNote(noteUuid);
      } catch (error) {
        console.error("[All Items] Error deleting note:", error);
      }
    },
    [handleDeleteNote]
  );

  if (!items.length) {
    return (
      <ItemsContainer>
        <Typography category="body" weight="regular" size="medium" color="body">
          No recent activities to show.
        </Typography>
      </ItemsContainer>
    );
  }

  return (
    <>
      <ItemsContainer>
        {items.map((item) => {
          if (item.type === "Email") {
            return (
              <EmailItem
                key={`all-email-${item.data.id}`}
                info={item.data}
                onOpen={handleOpenEmailSlideOut}
              />
            );
          }

          if (item.type === "Notes") {
            return (
              <NoteItem
                key={`all-note-${item.data.id}`}
                info={item.data}
                onOpen={handleOpenNotesSlideOut}
                onDelete={handleDeleteNoteClick}
              />
            );
          }

          if (item.type === "SMS") {
            return (
              <SMSItem
                key={`all-sms-${item.data.id}`}
                info={item.data}
                onOpen={handleOpenSMSSlideOut}
              />
            );
          }

          return null;
        })}
      </ItemsContainer>

      <EmailSlideOut
        open={showEmailSlideOut}
        onClose={handleCloseEmailSlideOut}
        email={selectedEmail}
      />

      <NotesSlideOut
        open={showNotesSlideOut}
        onClose={handleCloseNotesSlideOut}
        mode="update"
        noteData={selectedNote}
        onUpdateNote={async (noteData) => {
          try {
            await handleUpdateNote(noteData);
            setShowNotesSlideOut(false);
            setSelectedNote(null);
          } catch (error) {
            throw error;
          }
        }}
      />

      <SMSSlideOut
        open={showSMSSlideOut}
        onClose={handleCloseSMSSlideOut}
        sms={selectedSMS}
      />
    </>
  );
});

export default Items;
